<template>
  <div class="creative-page">
    <MenuBar />
    <div class="content">
      <div class="page-header">
        <h1>Creative Dashboard</h1>
        <p>Assigned campaign tasks, instruction, resources, and delivery upload workflow.</p>
      </div>

      <div class="summary">
        <div class="card metric done">
          <span>Completed This Month</span>
          <strong>{{ completedThisMonthCount }}</strong>
        </div>
        <div class="card metric assigned">
          <span>Assigned</span>
          <strong>{{ assignedCount }}</strong>
        </div>
        <div class="card metric approval">
          <span>Waiting For Approval</span>
          <strong>{{ waitingApprovalCount }}</strong>
        </div>
        <div class="card metric publishing">
          <span>Publishing Phase</span>
          <strong>{{ publishingCount }}</strong>
        </div>
      </div>

      <div class="panel board-panel">
        <div class="board-title-row">
          <h2>Notice Center</h2>
          <span class="board-meta">Active: {{ activeNoticeboardNotifications.length }}</span>
        </div>
        <div class="board-tabs">
          <button class="tab-btn" :class="{ active: noticeboardTab === 'active' }" @click="noticeboardTab = 'active'">
            Active ({{ activeNoticeboardNotifications.length }})
          </button>
          <button class="tab-btn" :class="{ active: noticeboardTab === 'history' }" @click="noticeboardTab = 'history'">
            Received History ({{ historyNoticeboardNotifications.length }})
          </button>
        </div>
        <div class="noticeboard" role="region" aria-label="Creative notifications board">
          <div v-if="!displayedNoticeboardNotifications.length" class="empty board-empty">
            {{ noticeboardTab === 'active' ? 'No active notifications' : 'No received notifications yet' }}
          </div>
          <div v-else class="notice-list">
            <article
              v-for="notification in displayedNoticeboardNotifications"
              :key="notification.id"
              class="notice"
              :class="[
                `notice-${notificationKind(notification)}`,
                { received: notification.isRead },
              ]"
            >
              <header class="notice-head">
                <span class="notice-pill">{{ notificationKindLabel(notification) }}</span>
                <small>{{ formatDate(notification.createdAt) }}</small>
              </header>
              <p class="notice-text" :title="notification.message">{{ notification.message }}</p>
              <div class="notice-actions">
                <button v-if="!notification.isRead" class="btn mini receive" @click="markNotificationRead(notification)">Receive</button>
                <button v-if="findAssignmentByNotification(notification)" class="btn mini trace" @click="traceNotification(notification)">Trace Task</button>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="panel">
        <h2>Assigned Tasks</h2>
        <div class="filters" v-if="assignments.length">
          <div class="filter-item">
            <label for="task-name-filter">Name</label>
            <input
              id="task-name-filter"
              v-model.trim="filters.name"
              type="text"
              placeholder="Search campaign name"
            />
          </div>
          <div class="filter-item">
            <label for="task-status-filter">Status</label>
            <select id="task-status-filter" v-model="filters.status">
              <option value="">All Status</option>
              <option value="creative_production">Creative Production</option>
              <option value="creative_approved">Creative Approved</option>
              <option value="publishing">Publishing</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div class="filter-item">
            <label for="task-start-filter">From Date</label>
            <input id="task-start-filter" v-model="filters.startDate" type="date" />
          </div>
          <div class="filter-item">
            <label for="task-end-filter">To Date</label>
            <input id="task-end-filter" v-model="filters.endDate" type="date" />
          </div>
          <div class="filter-actions">
            <button class="btn secondary" @click="resetFilters">Clear</button>
          </div>
        </div>

        <div v-if="!assignments.length" class="empty">No assigned tasks yet</div>
        <div v-else-if="!filteredAssignments.length" class="empty">No tasks found for selected filters</div>
        <div v-else class="task-table-wrap">
          <table class="task-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sponsor</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Work Status</th>
                <th class="actions-col">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in filteredAssignments" :key="task.id">
                <td>{{ task.campaignDetails.campaignName || '-' }}</td>
                <td>{{ task.campaignDetails.sponsorName || '-' }}</td>
                <td>{{ humanDate(task.campaignDetails.startDate) }}</td>
                <td>{{ humanDate(task.campaignDetails.endDate) }}</td>
                <td>
                  <span class="status" :class="`s-${task.status}`">{{ statusLabel(task.status) }}</span>
                </td>
                <td class="actions-col">
                  <button class="btn mini" @click="openDetails(task.id)">View Details</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="showDetailsModal && selectedTask" class="modal-overlay" @click.self="closeDetails">
        <div class="modal-card">
          <div class="modal-head">
            <h3>{{ selectedTask.campaignDetails.campaignName || 'Task Details' }}</h3>
            <button class="icon-btn" @click="closeDetails">Close</button>
          </div>

          <div class="task-grid modal-grid">
            <p><strong>Sponsor:</strong> {{ selectedTask.campaignDetails.sponsorName || '-' }}</p>
            <p><strong>Start:</strong> {{ humanDate(selectedTask.campaignDetails.startDate) }}</p>
            <p><strong>End:</strong> {{ humanDate(selectedTask.campaignDetails.endDate) }}</p>
            <p><strong>Total Content:</strong> {{ selectedTask.campaignDetails.numberOfContent || '-' }}</p>
            <p><strong>Status:</strong> {{ statusLabel(selectedTask.status) }}</p>
          </div>

          <p class="note"><strong>Instruction:</strong> {{ selectedTask.campaignDetails.instruction || '-' }}</p>
          <p class="note"><strong>Creative:</strong> {{ selectedTask.campaignDetails.creative || '-' }}</p>

          <div class="files">
            <p class="title">Campaign Resources</p>
            <div v-if="hasFiles(selectedTask.campaignDetails.fileRequirements)">
              <div v-for="(files, label) in selectedTask.campaignDetails.fileRequirements" :key="`${selectedTask.id}-${label}`" class="file-group" v-if="Array.isArray(files) && files.length">
                <span>{{ prettyLabel(label) }}</span>
                <ul>
                  <li v-for="file in files" :key="file.key || file.url">
                    <a :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name || 'File' }}</a>
                  </li>
                </ul>
              </div>
            </div>
            <p v-else class="empty-mini">No files</p>
          </div>

          <div class="deliverables">
            <p class="title">Upload Creative Deliverables</p>
            <input type="file" multiple :ref="`deliverable-${selectedTask.id}`" class="hidden" @change="queueDeliverables($event, selectedTask)" />
            <div class="deliverable-toolbar">
              <button class="btn mini" @click="openDeliverablePicker(selectedTask.id)">Select Files</button>
              <button v-if="getPendingDeliverables(selectedTask.id).length" class="btn mini secondary" @click="clearPendingDeliverables(selectedTask.id)">Clear Selection</button>
            </div>
            <p class="hint">Allowed: mp4, mov, png, jpg, psd, pdf, zip</p>

            <div v-if="getPendingDeliverables(selectedTask.id).length" class="pending-upload-panel">
              <p class="title queued-title">Selected Files Preview</p>
              <div class="pending-preview-grid">
                <div v-for="file in getPendingDeliverables(selectedTask.id)" :key="file.localId" class="pending-card">
                  <div class="pending-preview" :class="`kind-${file.previewKind}`">
                    <img v-if="file.previewKind === 'image'" :src="file.previewUrl" :alt="file.name" class="pending-image" />
                    <video v-else-if="file.previewKind === 'video'" :src="file.previewUrl" class="pending-video" controls preload="metadata"></video>
                    <div v-else class="pending-file-icon">{{ file.extensionLabel }}</div>
                  </div>
                  <div class="pending-info">
                    <strong>{{ file.name }}</strong>
                    <span>{{ file.extensionLabel }} • {{ formatFileSize(file.size) }}</span>
                  </div>
                  <!-- Per-file progress bar (visible while uploading) -->
                  <template v-if="uploadProgressByTask[selectedTask.id] && uploadProgressByTask[selectedTask.id][file.localId]">
                    <div class="cd-progress-bar-track">
                      <div
                        class="cd-progress-bar-fill"
                        :style="{ width: (uploadProgressByTask[selectedTask.id][file.localId].progress || 0) + '%' }"
                        :class="{
                          done: uploadProgressByTask[selectedTask.id][file.localId].status === 'done',
                          error: uploadProgressByTask[selectedTask.id][file.localId].status === 'error'
                        }"
                      ></div>
                    </div>
                    <span class="cd-progress-pct" :class="uploadProgressByTask[selectedTask.id][file.localId].status">
                      <template v-if="uploadProgressByTask[selectedTask.id][file.localId].status === 'done'">✓ Done</template>
                      <template v-else-if="uploadProgressByTask[selectedTask.id][file.localId].status === 'error'">✗ Error</template>
                      <template v-else>{{ uploadProgressByTask[selectedTask.id][file.localId].progress || 0 }}%</template>
                    </span>
                  </template>
                  <button
                    class="btn mini danger"
                    :disabled="uploadingTaskId === selectedTask.id"
                    @click="removePendingDeliverable(selectedTask.id, file.localId)"
                  >Remove</button>
                </div>
              </div>
              <div class="submit-row">
                <p class="hint">Files stay here until you press Submit to DCM.</p>
                <button class="btn submit-btn" :disabled="uploadingTaskId === selectedTask.id" @click="submitPendingDeliverables(selectedTask)">
                  {{ uploadingTaskId === selectedTask.id ? 'Submitting...' : 'Submit To DCM' }}
                </button>
              </div>
            </div>

            <ul v-if="Array.isArray(selectedTask.deliverables) && selectedTask.deliverables.length">
              <li v-for="file in selectedTask.deliverables" :key="file.key || file.url">
                <a :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name || 'Deliverable' }}</a>
              </li>
            </ul>
            <p v-else-if="!getPendingDeliverables(selectedTask.id).length" class="empty-mini">No deliverables uploaded yet</p>
          </div>

          <div class="actions">
            <p class="hint">After you click Submit To DCM, the selected files are uploaded and sent to DCM for review.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';
import { uploadFileInChunks } from '../services/chunkedUploadService';
import { campaignStatusLabel } from '../utils/campaignLifecycle';

export default {
  name: 'CreativeDashboard',
  components: { MenuBar },
  data() {
    return {
      assignments: [],
      notifications: [],
      unreadNotifications: 0,
      message: '',
      pollTimer: null,
      showDetailsModal: false,
      selectedTaskId: null,
      pendingDeliverablesByTask: {},
      uploadingTaskId: '',
      uploadProgressByTask: {}, // { [taskId]: { [localId]: { name, progress, status } } }
      noticeboardTab: 'active',
      filters: {
        name: '',
        status: '',
        startDate: '',
        endDate: '',
      },
    };
  },
  computed: {
    selectedTask() {
      return this.assignments.find(item => item.id === this.selectedTaskId) || null;
    },
    completedThisMonthCount() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      return this.assignments.filter(task => {
        if (String(task.status || '') !== 'completed') {
          return false;
        }

        const completedMs = this.resolveCompletedMs(task);
        if (completedMs === null) {
          return false;
        }

        const completedDate = new Date(completedMs);
        return completedDate.getFullYear() === currentYear && completedDate.getMonth() === currentMonth;
      }).length;
    },
    assignedCount() {
      return this.assignments.filter(task => {
        const status = String(task.status || '');
        return status === 'task_accepted' || status === 'creative_production';
      }).length;
    },
    waitingApprovalCount() {
      return this.assignments.filter(task => String(task.status || '') === 'creative_approved').length;
    },
    publishingCount() {
      return this.assignments.filter(task => String(task.status || '') === 'publishing').length;
    },
    noticeboardNotifications() {
      return this.notifications.filter(notification => {
        const kind = this.notificationKind(notification);
        return kind === 'task' || kind === 'feedback';
      });
    },
    activeNoticeboardNotifications() {
      return this.noticeboardNotifications.filter(notification => !notification.isRead);
    },
    historyNoticeboardNotifications() {
      return this.noticeboardNotifications.filter(notification => notification.isRead);
    },
    displayedNoticeboardNotifications() {
      if (this.noticeboardTab === 'history') {
        return this.historyNoticeboardNotifications;
      }
      return this.activeNoticeboardNotifications;
    },
    filteredAssignments() {
      return this.assignments.filter(task => {
        const campaignName = String((task.campaignDetails && task.campaignDetails.campaignName) || '').toLowerCase();
        const taskStatus = String(task.status || '');

        if (this.filters.name && !campaignName.includes(this.filters.name.toLowerCase())) {
          return false;
        }

        if (this.filters.status && taskStatus !== this.filters.status) {
          return false;
        }

        const startMs = this.parseDateMs(task.campaignDetails && task.campaignDetails.startDate);
        const endMs = this.parseDateMs(task.campaignDetails && task.campaignDetails.endDate);

        if (this.filters.startDate) {
          const fromMs = this.parseDateMs(this.filters.startDate, true);
          if (fromMs !== null) {
            if (startMs !== null) {
              if (startMs < fromMs) return false;
            } else if (endMs !== null && endMs < fromMs) {
              return false;
            }
          }
        }

        if (this.filters.endDate) {
          const toMs = this.parseDateMs(this.filters.endDate, true, true);
          if (toMs !== null) {
            if (endMs !== null) {
              if (endMs > toMs) return false;
            } else if (startMs !== null && startMs > toMs) {
              return false;
            }
          }
        }

        return true;
      });
    },
  },
  mounted() {
    this.loadAll();
    this.pollTimer = setInterval(() => {
      this.loadAll(true);
    }, 15000);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.releaseAllPendingDeliverables();
  },
  methods: {
    async loadAll(isSilent = false) {
      if (!isSilent) this.message = '';
      try {
        const [assignmentsRes, notificationsRes] = await Promise.all([
          sponsorService.getCreativeAssignments(),
          sponsorService.getCreativeNotifications(),
        ]);
        this.assignments = assignmentsRes.items || [];
        this.notifications = notificationsRes.items || [];
        this.unreadNotifications = Number(notificationsRes.unreadCount || 0);
      } catch (error) {
        if (!isSilent) {
          this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load creative dashboard data';
        }
      }
    },
    openDeliverablePicker(assignmentId) {
      const refName = `deliverable-${assignmentId}`;
      const refValue = this.$refs[refName];
      const input = Array.isArray(refValue) ? refValue[0] : refValue;
      if (input) input.click();
    },
    openDetails(taskId) {
      this.selectedTaskId = taskId;
      this.showDetailsModal = true;
    },
    closeDetails() {
      if (this.selectedTaskId) {
        this.clearPendingDeliverables(this.selectedTaskId);
      }
      this.showDetailsModal = false;
      this.selectedTaskId = null;
    },
    parseDateMs(value, assumeLocalDate = false, endOfDay = false) {
      if (!value) return null;

      if (assumeLocalDate && typeof value === 'string') {
        const parts = value.split('-').map(Number);
        if (parts.length === 3 && parts.every(num => Number.isFinite(num))) {
          const [year, month, day] = parts;
          const date = endOfDay
            ? new Date(year, month - 1, day, 23, 59, 59, 999)
            : new Date(year, month - 1, day, 0, 0, 0, 0);
          const time = date.getTime();
          return Number.isNaN(time) ? null : time;
        }
      }

      const date = new Date(value);
      const time = date.getTime();
      return Number.isNaN(time) ? null : time;
    },
    resolveCompletedMs(task) {
      const completedCandidates = [
        task && task.completedAt,
        task && task.updatedAt,
        task && task.campaignDetails && task.campaignDetails.completedAt,
        task && task.campaignDetails && task.campaignDetails.updatedAt,
        task && task.campaignDetails && task.campaignDetails.endDate,
      ];

      for (const value of completedCandidates) {
        const ms = this.parseDateMs(value);
        if (ms !== null) {
          return ms;
        }
      }

      return null;
    },
    notificationKind(notification) {
      const typed = String((notification && notification.notificationType) || '').toLowerCase();
      if (typed === 'task_assignment') {
        return 'task';
      }
      if (typed === 'feedback' || typed === 'feedback_revision') {
        return 'feedback';
      }

      const message = String((notification && notification.message) || '').toLowerCase();
      if (message.includes('feedback') || message.includes('resubmit')) {
        return 'feedback';
      }
      if (message.includes('new campaign task') || message.includes('assigned')) {
        return 'task';
      }
      return '';
    },
    notificationKindLabel(notification) {
      const kind = this.notificationKind(notification);
      if (kind === 'feedback') {
        return 'Feedback';
      }
      return 'New Task';
    },
    findAssignmentByNotification(notification) {
      const assignmentId = String((notification && notification.assignmentId) || '');
      if (!assignmentId) return null;
      return this.assignments.find(item => String(item.id) === assignmentId) || null;
    },
    traceNotification(notification) {
      const assignment = this.findAssignmentByNotification(notification);
      if (!assignment) return;
      this.openDetails(assignment.id);
    },
    resetFilters() {
      this.filters = {
        name: '',
        status: '',
        startDate: '',
        endDate: '',
      };
    },
    getPendingDeliverables(taskId) {
      return this.pendingDeliverablesByTask[String(taskId)] || [];
    },
    queueDeliverables(event, task) {
      const files = Array.from((event && event.target && event.target.files) || []);
      event.target.value = '';
      if (!files.length) return;

      const MAX_SIZE = 5 * 1024 * 1024 * 1024;
      const oversized = files.find(f => f.size > MAX_SIZE);
      if (oversized) {
        this.message = `"${oversized.name}" exceeds the 5 GB upload limit.`;
        return;
      }

      const taskKey = String(task.id);
      const existingItems = this.getPendingDeliverables(taskKey);
      const nextItems = files.map(file => this.buildPendingDeliverable(file));
      this.pendingDeliverablesByTask = {
        ...this.pendingDeliverablesByTask,
        [taskKey]: [...existingItems, ...nextItems],
      };
    },
    buildPendingDeliverable(file) {
      const extension = String((file.name || '').split('.').pop() || '').toUpperCase();
      const mimeType = String(file.type || '').toLowerCase();
      let previewKind = 'file';
      if (mimeType.startsWith('image/')) {
        previewKind = 'image';
      } else if (mimeType.startsWith('video/')) {
        previewKind = 'video';
      }

      return {
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        file,
        name: file.name || 'File',
        size: Number(file.size || 0),
        previewUrl: URL.createObjectURL(file),
        previewKind,
        extensionLabel: extension || 'FILE',
      };
    },
    removePendingDeliverable(taskId, localId) {
      const taskKey = String(taskId);
      const currentItems = this.getPendingDeliverables(taskKey);
      const target = currentItems.find(item => item.localId === localId);
      if (!target) return;

      this.releasePendingItem(target);
      const remaining = currentItems.filter(item => item.localId !== localId);
      this.pendingDeliverablesByTask = {
        ...this.pendingDeliverablesByTask,
        [taskKey]: remaining,
      };
    },
    clearPendingDeliverables(taskId) {
      const taskKey = String(taskId);
      const currentItems = this.getPendingDeliverables(taskKey);
      currentItems.forEach(this.releasePendingItem);
      this.pendingDeliverablesByTask = {
        ...this.pendingDeliverablesByTask,
        [taskKey]: [],
      };
    },
    releasePendingItem(item) {
      if (item && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    },
    releaseAllPendingDeliverables() {
      Object.values(this.pendingDeliverablesByTask || {}).forEach(items => {
        if (Array.isArray(items)) {
          items.forEach(this.releasePendingItem);
        }
      });
      this.pendingDeliverablesByTask = {};
    },
    async submitPendingDeliverables(task) {
      const taskKey = String(task.id);
      const pendingItems = this.getPendingDeliverables(taskKey);
      if (!pendingItems.length || this.uploadingTaskId === taskKey) return;

      try {
        this.uploadingTaskId = taskKey;

        // Initialise progress map for all pending files
        const progressMap = {};
        pendingItems.forEach(item => {
          progressMap[item.localId] = { name: item.name, progress: 0, status: 'uploading' };
        });
        this.$set(this.uploadProgressByTask, taskKey, { ...progressMap });

        for (const pendingItem of pendingItems) {
          try {
            await uploadFileInChunks(pendingItem.file, {
              context: 'creative_deliverable',
              assignmentId: task.id,
              onProgress: (pct) => {
                const current = this.uploadProgressByTask[taskKey] || {};
                this.$set(this.uploadProgressByTask, taskKey, {
                  ...current,
                  [pendingItem.localId]: { ...current[pendingItem.localId], progress: pct },
                });
              },
            });

            const current = this.uploadProgressByTask[taskKey] || {};
            this.$set(this.uploadProgressByTask, taskKey, {
              ...current,
              [pendingItem.localId]: { ...current[pendingItem.localId], progress: 100, status: 'done' },
            });
          } catch (err) {
            const current = this.uploadProgressByTask[taskKey] || {};
            this.$set(this.uploadProgressByTask, taskKey, {
              ...current,
              [pendingItem.localId]: { ...current[pendingItem.localId], status: 'error' },
            });
            this.message = err.message || `Failed to upload "${pendingItem.name}"`;
          }
        }

        this.message = 'Creative files uploaded and sent to DCM successfully';
        this.clearPendingDeliverables(taskKey);
        await this.loadAll(true);
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to submit deliverables to DCM';
      } finally {
        this.uploadingTaskId = '';
      }
    },
    async markNotificationRead(notification) {
      try {
        await sponsorService.markCreativeNotificationRead(notification.id);
        await this.loadAll(true);
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to mark notification';
      }
    },
    prettyLabel(label) {
      return String(label || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    },
    hasFiles(fileRequirements) {
      if (!fileRequirements || typeof fileRequirements !== 'object') return false;
      return Object.values(fileRequirements).some(files => Array.isArray(files) && files.length);
    },
    statusLabel(status) {
      return campaignStatusLabel(status);
    },
    humanDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatFileSize(size) {
      const value = Number(size || 0);
      if (value < 1024) return `${value} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
      return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
  },
};
</script>

<style scoped>
.creative-page { display: flex; min-height: 100vh; background: #f4f7fb; }
.content { flex: 1; margin-left: 250px; padding: 24px; }
.page-header h1 { margin: 0; color: #1f2d3d; }
.page-header p { margin: 8px 0 14px; color: #5f7389; }
.summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.card { border: 1px solid #dfe7f2; background: #fff; border-radius: 12px; padding: 12px; }
.metric { position: relative; overflow: hidden; }
.metric::before { content: ''; position: absolute; inset: 0 0 auto; height: 4px; }
.metric.done::before { background: #16a34a; }
.metric.assigned::before { background: #ef4444; }
.metric.approval::before { background: #d97706; }
.metric.publishing::before { background: #0ea5e9; }
.card span { display: block; color: #647a92; font-size: 12px; }
.card strong { font-size: 24px; color: #1f3654; }
.panel { border: 1px solid #dfe7f2; border-radius: 12px; background: #fff; padding: 14px; margin-bottom: 12px; }
.panel h2 { margin: 0 0 10px; color: #233244; }
.board-panel { padding-bottom: 18px; }
.board-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.board-meta { font-size: 12px; color: #495d76; font-weight: 700; }
.board-tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.tab-btn {
  border: 1px solid #ccd8ea;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  background: #f6f9ff;
  color: #3d4f69;
  cursor: pointer;
}
.tab-btn.active { background: #1e3a8a; border-color: #1e3a8a; color: #fff; }
.noticeboard {
  height: clamp(320px, 50vh, 430px);
  border-radius: 14px;
  border: 1px solid #d5e1f0;
  background:
    radial-gradient(circle at 18% 8%, rgba(255, 255, 255, 0.85) 0, rgba(255, 255, 255, 0) 42%),
    radial-gradient(circle at 84% 92%, rgba(198, 221, 255, 0.45) 0, rgba(198, 221, 255, 0) 40%),
    linear-gradient(180deg, #f7fbff 0%, #edf3fb 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 10px 18px rgba(36, 66, 110, 0.14);
  padding: 12px;
  overflow-y: auto;
}
.notice-list { display: grid; gap: 10px; }
.notice {
  border-radius: 10px;
  border: 1px solid #d8e2f0;
  padding: 10px;
  box-shadow: 0 4px 10px rgba(36, 66, 110, 0.12);
}
.notice-task { background: #fff0f1; border-left: 5px solid #e11d48; }
.notice-feedback { background: #fff8dc; border-left: 5px solid #d97706; }
.notice.received { opacity: 0.9; }
.notice-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.notice-pill { font-size: 10px; font-weight: 800; letter-spacing: 0.02em; text-transform: uppercase; color: #1f2937; }
.notice-head small { color: #475569; font-size: 11px; }
.notice-text {
  margin: 0;
  color: #1f2937;
  font-size: 12px;
  line-height: 1.4;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notice-actions { margin-top: 8px; display: flex; gap: 8px; flex-wrap: wrap; }
.btn.mini.receive { background: #0f766e; }
.btn.mini.trace { background: #334155; }
.board-empty { color: #50627b; }
.deliverable-toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.pending-upload-panel { margin-top: 10px; border: 1px solid #d8e2f0; border-radius: 12px; background: #f8fbff; padding: 12px; }
.queued-title { margin-bottom: 8px; }
.pending-preview-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
.pending-card { border: 1px solid #d7e3f3; border-radius: 12px; background: #fff; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.cd-progress-bar-track { height: 6px; border-radius: 4px; background: #e5e7eb; overflow: hidden; }
.cd-progress-bar-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, #4f6ef2, #7b9eff); transition: width 0.2s ease; }
.cd-progress-bar-fill.done { background: #22c55e; }
.cd-progress-bar-fill.error { background: #ef4444; }
.cd-progress-pct { font-size: 11px; font-weight: 600; color: #4f6ef2; text-align: right; }
.cd-progress-pct.done { color: #16a34a; }
.cd-progress-pct.error { color: #dc2626; }
.pending-preview { height: 120px; border-radius: 10px; background: #eef4fb; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.pending-preview.kind-file { border: 1px dashed #c6d5ea; }
.pending-image, .pending-video { width: 100%; height: 100%; object-fit: cover; }
.pending-file-icon { font-size: 22px; font-weight: 800; color: #365278; letter-spacing: 0.06em; }
.pending-info { display: flex; flex-direction: column; gap: 2px; }
.pending-info strong { color: #22324a; font-size: 12px; word-break: break-word; }
.pending-info span { color: #6b7f95; font-size: 11px; }
.submit-row { margin-top: 10px; display: flex; justify-content: space-between; gap: 10px; align-items: center; flex-wrap: wrap; }
.btn.submit-btn { background: #0f766e; }
.btn.danger { background: #dc2626; }
.btn:disabled { opacity: 0.65; cursor: not-allowed; }
.filters { display: grid; grid-template-columns: minmax(220px, 1.4fr) minmax(180px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) auto; gap: 10px; margin-bottom: 12px; align-items: end; }
.filter-item { display: flex; flex-direction: column; gap: 4px; }
.filter-item label { font-size: 12px; color: #5f7389; font-weight: 700; }
.filter-item input, .filter-item select { border: 1px solid #cdd9e8; border-radius: 8px; padding: 7px 9px; font-size: 12px; color: #2a3f56; background: #fff; }
.filter-actions { display: flex; justify-content: flex-end; }
.task-table-wrap { overflow-x: auto; }
.task-table { width: 100%; border-collapse: collapse; min-width: 960px; }
.task-table th,
.task-table td { border-bottom: 1px solid #e3ebf5; text-align: left; padding: 10px 8px; font-size: 13px; color: #33495f; }
.task-table th { color: #1f3654; font-weight: 700; background: #f7faff; }
.task-table tbody tr:hover { background: #f9fcff; }
.actions-col { text-align: right; white-space: nowrap; }
.status { border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 700; }
.status.s-creative_production { background: #fff4e5; color: #9a5b00; }
.status.s-creative_approved { background: #f3f0ff; color: #6941c6; }
.status.s-publishing { background: #e0f2fe; color: #075985; }
.status.s-completed { background: #dcfce7; color: #166534; }
.task-grid { margin-top: 8px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 10px; }
.modal-grid { margin-top: 0; }
.task-grid p, .note { margin: 0; color: #556c84; font-size: 12px; }
.note { margin-top: 6px; }
.files, .deliverables { margin-top: 8px; border-top: 1px solid #e3ebf5; padding-top: 8px; }
.title { margin: 0 0 4px; font-size: 12px; font-weight: 700; color: #2a466b; }
.file-group span { font-size: 12px; color: #60778f; font-weight: 700; }
.file-group ul, .deliverables ul { margin: 2px 0 0; padding-left: 16px; }
.file-group li a, .deliverables li a { color: #1d4ed8; font-size: 12px; }
.hint { margin: 4px 0; color: #71869b; font-size: 11px; }
.actions { display: flex; gap: 8px; margin-top: 10px; }
.btn { border: none; background: #2563eb; color: #fff; border-radius: 8px; padding: 7px 10px; font-size: 12px; cursor: pointer; }
.btn.secondary { background: #6c757d; }
.btn.mini { margin-top: 6px; padding: 6px 9px; font-size: 11px; }
.hidden { display: none; }
.empty, .empty-mini { color: #6f849a; font-size: 12px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; padding: 24px; z-index: 1100; }
.modal-card { width: min(920px, 96vw); max-height: 90vh; overflow-y: auto; background: #fff; border-radius: 12px; border: 1px solid #d9e5f5; padding: 16px; box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2); }
.modal-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.modal-head h3 { margin: 0; color: #1f2d3d; }
.icon-btn { border: 1px solid #c6d5ea; background: #f8fbff; color: #274868; border-radius: 8px; padding: 6px 10px; font-size: 12px; cursor: pointer; }
@media (max-width: 768px) {
  .content { margin-left: 200px; padding: 16px; }
  .summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .filters { grid-template-columns: 1fr; }
  .filter-actions { justify-content: flex-start; }
  .task-grid { grid-template-columns: 1fr; }
  .submit-row { align-items: stretch; }
  .modal-overlay { padding: 12px; }
  .modal-card { max-height: 95vh; }
}
@media (max-width: 520px) {
  .summary { grid-template-columns: 1fr; }
  .noticeboard { height: 340px; }
  .board-tabs { flex-direction: column; }
  .tab-btn { width: 100%; text-align: center; }
  .deliverable-toolbar, .submit-row { flex-direction: column; align-items: stretch; }
}
</style>